import React, { useMemo } from 'react';
import { safeMemberDisplayName } from '../../../utils/displayName';

function memberName(member = {}) {
  return safeMemberDisplayName(member, 'Member');
}

export default function AdminPermissionsCard({
  roles = [],
  assignments = [],
  memberProfiles = [],
  fullAdminIds = [],
  onEdit,
}) {
  const membersById = useMemo(
    () => new Map(memberProfiles.map((member) => [member.id, member])),
    [memberProfiles]
  );
  const fullAdminCount = Array.isArray(fullAdminIds) ? fullAdminIds.filter(Boolean).length : 0;
  const assignedLimitedAdminCount = useMemo(() => (
    new Set((assignments || []).map((assignment) => assignment.uid || assignment.id).filter(Boolean)).size
  ), [assignments]);
  const visibleRoles = roles.slice(0, 3);

  return (
    <div className="team-card admin-permissions-card">
      <div className="card-header-with-icon">
        <span className="card-title">Admin Permissions</span>
        <button type="button" className="btn btn--secondary btn--xs" onClick={onEdit}>
          Manage
        </button>
      </div>

      <div className="card-body admin-permissions-card__body">
        <div className="admin-permissions-card__metrics" aria-label="Admin permissions summary">
          <div className="admin-permissions-card__metric">
            <span className="admin-permissions-card__metric-value">{fullAdminCount}</span>
            <span className="admin-permissions-card__metric-label">Global Admins</span>
          </div>
          <div className="admin-permissions-card__metric">
            <span className="admin-permissions-card__metric-value">{roles.length}</span>
            <span className="admin-permissions-card__metric-label">Limited Roles</span>
          </div>
          <div className="admin-permissions-card__metric">
            <span className="admin-permissions-card__metric-value">{assignedLimitedAdminCount}</span>
            <span className="admin-permissions-card__metric-label">Limited Admins</span>
          </div>
        </div>

        <div className="admin-permissions-card__section">
          <div className="admin-permissions-card__section-title">Roles</div>
          <div className="admin-permissions-card__chips">
            <span className="admin-permissions-card__chip admin-permissions-card__chip--locked">
              Global Admin
            </span>
            {visibleRoles.map((role) => (
              <span key={role.id} className="admin-permissions-card__chip">
                {role.name || 'Unnamed role'}
              </span>
            ))}
            {roles.length > visibleRoles.length && (
              <span className="admin-permissions-card__chip admin-permissions-card__chip--muted">
                +{roles.length - visibleRoles.length} more
              </span>
            )}
          </div>
        </div>

        <div className="admin-permissions-card__section">
          <div className="admin-permissions-card__section-title">Assigned access</div>
          {fullAdminCount === 0 && assignedLimitedAdminCount === 0 ? (
            <p className="text-muted admin-permissions-card__empty">No elevated admin access is assigned.</p>
          ) : (
            <ul className="admin-permissions-card__assigned-list">
              {fullAdminIds.slice(0, 2).map((uid) => {
                const member = membersById.get(uid) || {};
                return (
                  <li key={`global:${uid}`}>
                    <span>{memberName(member)}</span>
                    <strong>Global Admin</strong>
                  </li>
                );
              })}
              {assignments.slice(0, Math.max(0, 3 - Math.min(fullAdminIds.length, 2))).map((assignment) => {
                const uid = assignment.uid || assignment.id;
                const member = membersById.get(uid) || {};
                return (
                  <li key={`limited:${uid}`}>
                    <span>{memberName(member)}</span>
                    <strong>{assignment.roleName || 'Limited role'}</strong>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
